import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert } from 'antd';
import useWizard from '@components/Wizard/useWizard';
import { Card } from '@cards/Card';
import { FEATURE_FORM, FEATURE_QUESTIONNAIRE, FEATURE_TASK } from '@constants';
import AddRulesToRole from './AddRulesToRole';

function CareTeamPermissions() {
  const { t } = useTranslation();
  const [roleRules, setRoleRules] = useState([]);
  const [isInitialized, setIsInitialized] = useState(false);
  const { form, formData } = useWizard();

  const updateRoleRulesData = useCallback(
    data => {
      setRoleRules(data);
      form.setFieldsValue({ careTeamPermissions: data });
    },
    [form],
  );

  const extractRolesData = useCallback(rolesData => {
    if (!rolesData) return null;

    let current = rolesData;
    while (
      current &&
      typeof current === 'object' &&
      current.roles &&
      !Array.isArray(current.roles)
    ) {
      current = current.roles;
    }

    return current;
  }, []);

  const allAvailableRules = useMemo(() => {
    const stagedRules = formData.stagedRules?.stages?.flatMap(stage => stage.rules || []) || [];
    const indexEventRules =
      formData.indexEventRules?.indexEvents?.flatMap(ie => ie.rules || []) || [];

    const rulesMap = new Map();
    [...stagedRules, ...indexEventRules].forEach(rule => {
      if (
        !rulesMap.has(rule.id) &&
        [FEATURE_FORM, FEATURE_QUESTIONNAIRE, FEATURE_TASK].includes(rule.what)
      ) {
        rulesMap.set(rule.id, rule);
      }
    });

    return Array.from(rulesMap.values());
  }, [formData.stagedRules, formData.indexEventRules]);

  const callToActionList = useMemo(() => {
    return formData.callToAction || [];
  }, [formData.callToAction]);

  useEffect(() => {
    if (isInitialized) return;
    if (!formData.careTeamRoles) return;

    const extractedRolesData = extractRolesData(formData.careTeamRoles);
    if (!extractedRolesData) return;

    const roleKeys = Object.keys(extractedRolesData)
      .filter(key => key.match(/role-\d{2}--name/))
      .sort();

    if (roleKeys.length === 0) return;

    const savedRoleRules = formData.careTeamPermissions?.careTeamPermissions || [];

    const newRoleRules = roleKeys.map(nameKey => {
      const roleNumber = nameKey.match(/role-(\d{2})/)[1];
      const descKey = `role-${roleNumber}--description`;
      const name = extractedRolesData[nameKey] || '';
      const description = extractedRolesData[descKey] || '';

      const savedRole = savedRoleRules.find(r => r.name === name);

      return {
        name,
        description,
        permissions: savedRole ? savedRole.permissions : [],
      };
    });

    updateRoleRulesData(newRoleRules);
    setIsInitialized(true);
  }, [formData.careTeamRoles, formData.careTeamPermissions, updateRoleRulesData, extractRolesData, isInitialized]);

  useEffect(() => {
    if (!isInitialized) return;
    if (!formData.careTeamRoles) return;

    const extractedRolesData = extractRolesData(formData.careTeamRoles);
    if (!extractedRolesData) return;

    const roleKeys = Object.keys(extractedRolesData)
      .filter(key => key.match(/role-\d{2}--name/))
      .sort();

    if (roleKeys.length === 0) return;

    if (roleKeys.length !== roleRules.length) {
      const newRoleRules = roleKeys.map(nameKey => {
        const roleNumber = nameKey.match(/role-(\d{2})/)[1];
        const descKey = `role-${roleNumber}--description`;
        const name = extractedRolesData[nameKey] || '';
        const description = extractedRolesData[descKey] || '';

        const existingRole = roleRules.find(r => r.name === name);

        return {
          name,
          description,
          permissions: existingRole ? existingRole.permissions : [],
        };
      });

      updateRoleRulesData(newRoleRules);
      return;
    }

    const hasChanged = roleKeys.some((nameKey, index) => {
      const roleNumber = nameKey.match(/role-(\d{2})/)[1];
      const descKey = `role-${roleNumber}--description`;
      const name = extractedRolesData[nameKey] || '';
      const description = extractedRolesData[descKey] || '';

      return roleRules[index]?.name !== name || roleRules[index]?.description !== description;
    });

    if (hasChanged) {
      const updatedRoleRules = roleKeys.map((nameKey, index) => {
        const roleNumber = nameKey.match(/role-(\d{2})/)[1];
        const descKey = `role-${roleNumber}--description`;
        const name = extractedRolesData[nameKey] || '';
        const description = extractedRolesData[descKey] || '';

        return {
          name,
          description,
          permissions: roleRules[index]?.permissions || [],
        };
      });

      updateRoleRulesData(updatedRoleRules);
    }
  }, [formData.careTeamRoles, roleRules, updateRoleRulesData, extractRolesData, isInitialized]);

  const addRules = useCallback(
    roleIndex => permissions => {
      updateRoleRulesData([
        ...roleRules.slice(0, roleIndex),
        {
          ...roleRules[roleIndex],
          permissions: [...roleRules[roleIndex].permissions, ...permissions],
        },
        ...roleRules.slice(roleIndex + 1),
      ]);
    },
    [roleRules, updateRoleRulesData],
  );

  const removePermission = useCallback(
    roleIndex => permissionId => {
      updateRoleRulesData([
        ...roleRules.slice(0, roleIndex),
        {
          ...roleRules[roleIndex],
          permissions: roleRules[roleIndex].permissions.filter(
            permission => (permission.id || permission.rule?.id) !== permissionId,
          ),
        },
        ...roleRules.slice(roleIndex + 1),
      ]);
    },
    [roleRules, updateRoleRulesData],
  );

  const updatePermissionType = useCallback(
    roleIndex => (permissionId, newPermissionType) => {
      updateRoleRulesData([
        ...roleRules.slice(0, roleIndex),
        {
          ...roleRules[roleIndex],
          permissions: roleRules[roleIndex].permissions.map(permission =>
            (permission.id || permission.rule?.id) === permissionId
              ? { ...permission, permissionType: newPermissionType }
              : permission,
          ),
        },
        ...roleRules.slice(roleIndex + 1),
      ]);
    },
    [roleRules, updateRoleRulesData],
  );

  console.log('formData', formData);

  return (
    <>
      <Card>
        <p className="info-text">{t('pathways:ProcedurePathways.wizard.careTeamRules.info')}</p>
      </Card>
      {form.getFieldDecorator('careTeamPermissions')(<span />)}
      {roleRules.length === 0 ? (
        <Card.Half>
          <Alert
            description={t('pathways:ProcedurePathways.wizard.careTeamRules.noRoles.description')}
            message={t('pathways:ProcedurePathways.wizard.careTeamRules.noRoles.title')}
            showIcon
            type="info"
          />
        </Card.Half>
      ) : (
        roleRules.map(({ name, permissions }, index) => (
          <AddRulesToRole
            allAvailableRules={allAvailableRules}
            callToActionList={callToActionList}
            customRules={allAvailableRules}
            key={`role-${index}`}
            selectedPermissions={permissions}
            title={name}
            addPermissions={addRules(index)}
            removePermission={removePermission(index)}
            updatePermissionType={updatePermissionType(index)}
          />
        ))
      )}
    </>
  );
}

export default CareTeamPermissions;
